import React from "react";
import { Typography } from "@mui/joy";
import { EditRounded, DeleteRounded } from "@mui-symbols-material/w300";
import { useTranslation } from "react-i18next";
import EntityCard from "../EntityCard";
import CardActionButton from "../EntityCard/CardActionButton";
import CardTag from "../EntityCard/CardTag";
import { getAuditLine } from "../../utils/auditLine";
import { IotAlertSeverity, IotMaintenanceType } from "../../codegen/graphql";

export interface NotificationSubscriptionLike {
    id: string;
    title: string;
    breadcrumb: string;
    inApp: boolean;
    mail: boolean;
    push: boolean;
    iotAlert: boolean;
    iotAlertSeverity?: IotAlertSeverity | null;
    /** Maintenance-due opt-in; only node and region subscriptions carry it. */
    maintenanceDue?: boolean;
    /** Threshold: WARNING covers the later escalation to ERROR (overdue). */
    maintenanceSeverity?: IotAlertSeverity | null;
    /** `null`/undefined = every maintenance kind. */
    maintenanceType?: IotMaintenanceType | null;
    /** SLA status change opt-in (only known to global/region admins). */
    slaStatusChange?: boolean;
    insert?: string | null;
    update?: string | null;
    /** Region subscriptions are marked with a "Region" tag. */
    isRegion?: boolean;
}

interface Props {
    subscription: NotificationSubscriptionLike;
    onEdit: () => void;
    onDelete: () => void;
}

const NotificationSubscriptionCard = ({ subscription, onEdit, onDelete }: Props) => {
    const { t, i18n } = useTranslation();

    const isError = subscription.iotAlertSeverity === IotAlertSeverity.ERROR;
    const isMaintenanceOverdueOnly =
        subscription.maintenanceSeverity === IotAlertSeverity.ERROR;
    const maintenanceTypeLabel =
        subscription.maintenanceType === IotMaintenanceType.MAINTENANCE
            ? t("Maintenance only")
            : subscription.maintenanceType === IotMaintenanceType.REMANUFACTURE
                ? t("Remanufacturing only")
                : null;

    const auditLine = getAuditLine({
        insert: subscription.insert,
        update: subscription.update,
        t,
        language: i18n.language,
    });

    return (
        <EntityCard
            title={subscription.title || (subscription.isRegion ? t("Untitled region") : t("Untitled node"))}
            tags={
                <>
                    {subscription.isRegion && <CardTag label={t("Region")} />}
                    {subscription.inApp && <CardTag label={t("In-app")} />}
                    {subscription.mail && <CardTag label={t("Email")} />}
                    {subscription.push && <CardTag label={t("Push")} />}
                    {subscription.iotAlert && (
                        <CardTag
                            label={`${t("IoT")} · ${isError ? t("Alarm") : t("Warning")}`}
                            color={isError ? "danger" : "warning"}
                        />
                    )}
                    {subscription.maintenanceDue && (
                        <CardTag
                            label={`${t("Maintenance Due")} · ${
                                isMaintenanceOverdueOnly ? t("Overdue") : t("Warning")
                            }`}
                            color={isMaintenanceOverdueOnly ? "danger" : "warning"}
                        />
                    )}
                    {subscription.maintenanceDue && maintenanceTypeLabel && (
                        <CardTag label={maintenanceTypeLabel} />
                    )}
                    {subscription.slaStatusChange && <CardTag label={t("SLA")} />}
                </>
            }
            footer={auditLine}
            actions={
                <>
                    <CardActionButton tooltip={t("Edit")} icon={<EditRounded />} color="neutral" onClick={onEdit} />
                    <CardActionButton tooltip={t("Delete")} icon={<DeleteRounded />} color="danger" onClick={onDelete} />
                </>
            }
        >
            {subscription.breadcrumb && (
                <Typography level="body-sm" color="neutral">
                    {subscription.breadcrumb}
                </Typography>
            )}
        </EntityCard>
    );
};

export default NotificationSubscriptionCard;
